import { CARRIER_TRANSPORTATION_COST_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF } from './approved-source-systems';
import type {
  ApprovedFormulaDefinition,
  ApprovedFormulaEvidenceArtifact,
  ApprovedFormulaEvidenceValidationBlock,
  ApprovedFormulaGoldenTrace,
  ApprovedFormulaImplementationDigestInput,
  ApprovedFormulaReconciliationRule,
  ApprovedFormulaResult,
} from './approved-formulas';

/**
 * CARRIER TRANSPORTATION SCENARIO FORMULAS — the unpark of chain-audit cluster
 * `formula-unregistered` (C7), SPEC-1..4 of the #4008 CHW park
 * (`.codex/reports/4008-chw/PARK-formula-unregistered.md`).
 *
 * Four `ObjectiveContributionContract.formulaRefs` in
 * `carrier-transportation-cost.manifest.ts` named deterministic scenario
 * operations that were never approved formulas: `size_carrier_exit_tradeoff`,
 * `size_mode_shift_tradeoff`, `score_specialist_network_options` and
 * `size_customer_repricing_volume_response`. Each operation computed a real
 * number inline in `carrier-transportation-cost.scenarios.ts` and published it
 * with `formulaCitations: []` — a governed claim with no formula behind it.
 *
 * These four definitions ARE that arithmetic, lifted verbatim. Nothing is
 * invented: every operator, rounding step and fallback below is the one the
 * module already executed, so promoting the number to a governed formula changes
 * the governance of the number, not the number.
 *
 * Sign convention for all four: the carrier manifest declares
 * `signConvention: 'positive_improves_metric'` and `unit: 'USD'` on every
 * contribution contract. A positive primary metric improves operating income.
 */

const CARRIER_TRANSPORTATION_FORMULA_VERSION = '1.0.0';
const FORMULA_RUNTIME_VERSION = 'approved_formula_runtime_v1';
const CANONICALIZER_VERSION = 'approved_formula_canonicalizer_v1';

/** Mirrors ASK_HELIX_CARRIER_TRANSPORTATION_COST_DATA_LAKE_CONTEXT_KIND. Held as
 * a literal here — as org-analysis holds its own — so the formula registry never
 * imports the ask-helix surface. */
const CARRIER_TRANSPORTATION_CONTEXT_KIND =
  'decision_agent:carrier_transportation_cost:governed_snapshot';

/**
 * The scenario-template ids these formulas govern. Kept as a first-class export
 * so the module seam and the chain auditor can assert the mapping instead of
 * re-deriving it from prose.
 */
export const CARRIER_TRANSPORTATION_SCENARIO_FORMULA_IDS = Object.freeze([
  'carrier_exit_tradeoff_v1',
  'mode_shift_tradeoff_v1',
  'specialist_network_option_score_v1',
  'customer_repricing_volume_response_v1',
] as const);

export type CarrierTransportationScenarioFormulaId =
  (typeof CARRIER_TRANSPORTATION_SCENARIO_FORMULA_IDS)[number];

/**
 * The deterministic operation each formula replaces, one-for-one. The operation
 * id remains the scenario template's own identity in
 * TRANSPORTATION_SCENARIO_TEMPLATE_CATALOG; the formula id is what the claim's
 * `formulaRefs` now cites.
 */
export const CARRIER_TRANSPORTATION_OPERATION_TO_FORMULA = Object.freeze({
  size_carrier_exit_tradeoff: 'carrier_exit_tradeoff_v1',
  size_mode_shift_tradeoff: 'mode_shift_tradeoff_v1',
  score_specialist_network_options: 'specialist_network_option_score_v1',
  size_customer_repricing_volume_response: 'customer_repricing_volume_response_v1',
} as const);

/* -------------------------------------------------------------------------- */
/* shared helpers                                                              */
/* -------------------------------------------------------------------------- */

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isFiniteNonNegative(value: unknown): value is number {
  return isFiniteNumber(value) && value >= 0;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isUnitInterval(value: unknown): value is number {
  return isFiniteNumber(value) && value >= 0 && value <= 1;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/** Identical to `roundCurrency` in carrier-transportation-cost.scenarios.ts. */
function roundUsd(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/** Identical to `roundMeasure` in carrier-transportation-cost.scenarios.ts. */
function roundMeasure(value: number): number {
  return Math.round((value + Number.EPSILON) * 10000) / 10000;
}

/**
 * Volume-weighted mean service risk, with the module's exact degenerate-case
 * fallback: when the weighting basis is zero the worst row wins, because a
 * zero-volume cut must never report a safer number than any row it contains.
 */
function volumeWeightedServiceRisk(
  rows: readonly Readonly<{ volume: number; serviceRiskScore: number }>[],
  totalVolume: number,
): number {
  if (totalVolume > 0) {
    return roundMeasure(
      rows.reduce((sum, row) => sum + row.serviceRiskScore * row.volume, 0) / totalVolume,
    );
  }
  return roundMeasure(Math.max(...rows.map((row) => row.serviceRiskScore)));
}

function canonicalJson(value: unknown): string {
  return JSON.stringify(sortKeysDeep(value));
}

function sortKeysDeep(value: unknown): unknown {
  if (Array.isArray(value)) return value.map((entry) => sortKeysDeep(entry));
  if (!isRecord(value)) return value;
  return Object.keys(value)
    .sort()
    .reduce<Record<string, unknown>>((acc, key) => {
      acc[key] = sortKeysDeep(value[key]);
      return acc;
    }, {});
}

function blocked<TOutput>(
  reasonCode: 'invalid_formula_input' | 'missing_formula_input' | 'never_blend_violation',
  message: string,
): ApprovedFormulaResult<TOutput> {
  return { status: 'blocked', reasonCode, message };
}

function ok<TOutput>(output: TOutput): ApprovedFormulaResult<TOutput> {
  return { status: 'ok', output: Object.freeze(output) };
}

/**
 * Evidence binding. The carrier snapshot artifact carries the reporting cut the
 * formula input claims; a mismatch is a reconciliation failure, never a silent
 * recompute against the wrong cut.
 *
 * NOTE (DB100, not this lane): the carrier module has a known PERIOD-SCOPING
 * defect in its rollup lanes. This check binds the formula to the cut named in
 * its own input; it deliberately does NOT attempt to repair or re-derive the
 * period, which is `rollupLanes` work outside this fence. A formula whose
 * correctness depends on the period scoping is therefore only as correct as the
 * cut handed to it — stated here so the dependency is on the record.
 */
function validateEvidenceInput(
  formulaId: string,
  input: unknown,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  if (!isRecord(input)) return null;
  const claimedCut = input.reportingCutAsOf;
  if (!isNonEmptyString(claimedCut)) return null;
  for (const artifact of artifacts) {
    const content = isRecord(artifact.contentJson) ? artifact.contentJson : null;
    if (!content) continue;
    const artifactCut = content.reportingCutAsOf ?? content.asOf;
    if (!isNonEmptyString(artifactCut)) continue;
    if (artifactCut !== claimedCut) {
      return {
        reasonCode: 'formula_reconciliation_failure',
        message: `${formulaId} input reporting cut ${claimedCut} does not match the governed carrier snapshot cut ${artifactCut}.`,
      };
    }
  }
  return null;
}
function implementationDigestInput(
  name: string,
  implementation: unknown,
): ApprovedFormulaImplementationDigestInput {
  return Object.freeze({
    name,
    source: String(implementation),
  });
}

function createDefinition<TInput, TOutput>(input: {
  formulaId: CarrierTransportationScenarioFormulaId;
  displayName: string;
  description: string;
  inputSchemaVersion: string;
  outputSchemaVersion: string;
  execute: (formulaInput: TInput) => ApprovedFormulaResult<TOutput>;
  goldenTraces: readonly ApprovedFormulaGoldenTrace<TInput, TOutput>[];
  helpers?: readonly Readonly<[string, unknown]>[];
}): ApprovedFormulaDefinition<TInput, TOutput> {
  const reconciliationRule: ApprovedFormulaReconciliationRule<TInput, TOutput> = Object.freeze({
    kind: 'input_output_evidence_reconciliation',
    ruleId: `${input.formulaId}_reconciles_to_evidence_v1`,
    description:
      'Formula output must equal a deterministic replay of the exact governed carrier transportation input.',
    validate(formulaInput, output, artifacts) {
      const evidenceBlock = validateEvidenceInput(input.formulaId, formulaInput, artifacts);
      if (evidenceBlock) return evidenceBlock;
      const replay = input.execute(formulaInput as TInput);
      if (replay.status === 'blocked' || canonicalJson(replay.output) !== canonicalJson(output)) {
        return {
          reasonCode: 'formula_reconciliation_failure',
          message: `${input.formulaId} output failed deterministic replay reconciliation.`,
        };
      }
      return null;
    },
  });
  const digestInputs = [
    implementationDigestInput(input.execute.name || input.formulaId, input.execute),
    implementationDigestInput('validateEvidenceInput', validateEvidenceInput),
    implementationDigestInput('canonicalJson', canonicalJson),
    implementationDigestInput('roundUsd', roundUsd),
    implementationDigestInput('roundMeasure', roundMeasure),
    ...(input.helpers ?? []).map(([name, helper]) => implementationDigestInput(name, helper)),
  ];
  return Object.freeze({
    formulaId: input.formulaId,
    version: CARRIER_TRANSPORTATION_FORMULA_VERSION,
    displayName: input.displayName,
    description: input.description,
    lifecycle: 'code_defined',
    formulaRuntimeVersion: FORMULA_RUNTIME_VERSION,
    canonicalizerVersion: CANONICALIZER_VERSION,
    inputSchemaVersion: input.inputSchemaVersion,
    outputSchemaVersion: input.outputSchemaVersion,
    expectedContextKind: CARRIER_TRANSPORTATION_CONTEXT_KIND,
    approvedSourceSystemRefs: Object.freeze([
      CARRIER_TRANSPORTATION_COST_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
    ]),
    implementationDigestInputs: Object.freeze(digestInputs),
    goldenTraces: Object.freeze(input.goldenTraces.map((trace) => Object.freeze(trace))),
    validateInputAgainstEvidence(formulaInput, artifacts) {
      return validateEvidenceInput(input.formulaId, formulaInput, artifacts);
    },
    reconciliationRule,
    execute: input.execute,
  });
}

/* -------------------------------------------------------------------------- */
/* SPEC-1 · carrier_exit_tradeoff_v1                                           */
/* -------------------------------------------------------------------------- */

export type CarrierExitTradeoffInput = Readonly<{
  carrier: string;
  reportingCutAsOf: string;
  shipments: readonly Readonly<{
    shipmentId: string;
    volume: number;
    carrierCostUsd: number;
    replacementCarrierCostUsd: number;
    carrierExitPenaltyUsd: number;
    carrierExitServiceRiskScore: number;
    currency: 'USD';
  }>[];
}>;

export type CarrierExitTradeoffOutput = Readonly<{
  primaryMetricKey: 'carrier_exit_net_margin_impact_usd';
  primaryMetricValue: number;
  carrier: string;
  shipmentCount: number;
  carrierExitCurrentCostUsd: number;
  carrierExitReplacementCostUsd: number;
  carrierExitContractPenaltyUsd: number;
  carrierExitNetMarginImpactUsd: number;
  carrierExitServiceRiskScore: number;
}>;

/**
 * Exit economics for one carrier over its governed shipment rows.
 *
 *   current      = SUM(carrierCostUsd)                  -- cost removed by exiting
 *   replacement  = SUM(replacementCarrierCostUsd)        -- cost incurred instead
 *   penalty      = MAX(carrierExitPenaltyUsd)            -- see below
 *   serviceRisk  = volume-weighted MEAN(serviceRiskScore)
 *   net          = current - replacement - penalty
 *
 * PENALTY IS A MAX, NOT A SUM. The contract penalty is a single carrier-level
 * termination charge that every shipment row of that carrier carries a copy of;
 * summing it would multiply one penalty by the row count. This is the module's
 * existing behaviour and it is the correct one.
 *
 * Every row must carry penalty, replacement cost and service risk. The module
 * drops a carrier whose rows are not all covered rather than exiting on partial
 * evidence; here that same rule is a fail-closed block, so a partially-evidenced
 * carrier can never publish a net impact.
 */
function executeCarrierExitTradeoff(
  input: CarrierExitTradeoffInput,
): ApprovedFormulaResult<CarrierExitTradeoffOutput> {
  if (
    !input ||
    !isNonEmptyString(input.carrier) ||
    !isNonEmptyString(input.reportingCutAsOf) ||
    !Array.isArray(input.shipments) ||
    input.shipments.length === 0
  ) {
    return blocked(
      'missing_formula_input',
      'Carrier exit tradeoff requires a carrier, a reporting cut, and at least one governed shipment row.',
    );
  }
  const seen = new Set<string>();
  for (const row of input.shipments) {
    if (
      !row ||
      !isNonEmptyString(row.shipmentId) ||
      seen.has(row.shipmentId) ||
      row.currency !== 'USD' ||
      !isFiniteNonNegative(row.volume) ||
      !isFiniteNonNegative(row.carrierCostUsd) ||
      !isFiniteNonNegative(row.replacementCarrierCostUsd) ||
      !isFiniteNonNegative(row.carrierExitPenaltyUsd) ||
      !isUnitInterval(row.carrierExitServiceRiskScore)
    ) {
      return blocked(
        'invalid_formula_input',
        'Carrier exit shipment rows must carry unique ids, USD amounts that are finite and non-negative, and a service-risk score within [0,1].',
      );
    }
    seen.add(row.shipmentId);
  }
  const carrierExitCurrentCostUsd = roundUsd(
    input.shipments.reduce((sum, row) => sum + row.carrierCostUsd, 0),
  );
  const carrierExitReplacementCostUsd = roundUsd(
    input.shipments.reduce((sum, row) => sum + row.replacementCarrierCostUsd, 0),
  );
  const carrierExitContractPenaltyUsd = roundUsd(
    Math.max(...input.shipments.map((row) => row.carrierExitPenaltyUsd)),
  );
  const totalVolume = input.shipments.reduce((sum, row) => sum + row.volume, 0);
  const carrierExitServiceRiskScore = volumeWeightedServiceRisk(
    input.shipments.map((row) => ({
      volume: row.volume,
      serviceRiskScore: row.carrierExitServiceRiskScore,
    })),
    totalVolume,
  );
  const carrierExitNetMarginImpactUsd = roundUsd(
    carrierExitCurrentCostUsd - carrierExitReplacementCostUsd - carrierExitContractPenaltyUsd,
  );
  return ok({
    primaryMetricKey: 'carrier_exit_net_margin_impact_usd',
    primaryMetricValue: carrierExitNetMarginImpactUsd,
    carrier: input.carrier,
    shipmentCount: input.shipments.length,
    carrierExitCurrentCostUsd,
    carrierExitReplacementCostUsd,
    carrierExitContractPenaltyUsd,
    carrierExitNetMarginImpactUsd,
    carrierExitServiceRiskScore,
  });
}

const carrierExitTradeoffDefinition = createDefinition({
  formulaId: 'carrier_exit_tradeoff_v1',
  displayName: 'Carrier exit tradeoff',
  description:
    'Sizes the operating-income impact of exiting one carrier: governed current lane cost removed, less the replacement-carrier cost incurred, less the single contract termination penalty, with a volume-weighted service-risk score. Penalty is the carrier-level maximum, never a per-shipment sum.',
  inputSchemaVersion: 'carrier_exit_tradeoff_input_v1',
  outputSchemaVersion: 'carrier_exit_tradeoff_output_v1',
  execute: executeCarrierExitTradeoff,
  helpers: [
    ['isFiniteNonNegative', isFiniteNonNegative],
    ['isNonEmptyString', isNonEmptyString],
    ['isUnitInterval', isUnitInterval],
    ['volumeWeightedServiceRisk', volumeWeightedServiceRisk],
  ],
  goldenTraces: [
    {
      // Exit is accretive: 900k of lane cost removed, 700k replacement, 50k
      // penalty -> +150k to operating income.
      traceId: 'carrier_exit_tradeoff_v1:exit-accretive:v1',
      input: {
        carrier: 'Ridgeline Freight',
        reportingCutAsOf: '2026-07-31',
        shipments: [
          {
            shipmentId: 'shp-1',
            volume: 600,
            carrierCostUsd: 500_000,
            replacementCarrierCostUsd: 400_000,
            carrierExitPenaltyUsd: 50_000,
            carrierExitServiceRiskScore: 0.2,
            currency: 'USD',
          },
          {
            shipmentId: 'shp-2',
            volume: 400,
            carrierCostUsd: 400_000,
            replacementCarrierCostUsd: 300_000,
            carrierExitPenaltyUsd: 50_000,
            carrierExitServiceRiskScore: 0.45,
            currency: 'USD',
          },
        ],
      },
      expected: {
        status: 'ok',
        output: {
          primaryMetricKey: 'carrier_exit_net_margin_impact_usd',
          primaryMetricValue: 150_000,
          carrier: 'Ridgeline Freight',
          shipmentCount: 2,
          carrierExitCurrentCostUsd: 900_000,
          carrierExitReplacementCostUsd: 700_000,
          carrierExitContractPenaltyUsd: 50_000,
          carrierExitNetMarginImpactUsd: 150_000,
          // (0.2*600 + 0.45*400) / 1000 = 0.3
          carrierExitServiceRiskScore: 0.3,
        },
      },
    },
    {
      // The penalty dominates the saving. The net stays negative — exiting is
      // value-destroying and the formula says so rather than flooring at zero.
      traceId: 'carrier_exit_tradeoff_v1:penalty-dominates:v1',
      input: {
        carrier: 'Ridgeline Freight',
        reportingCutAsOf: '2026-07-31',
        shipments: [
          {
            shipmentId: 'shp-1',
            volume: 100,
            carrierCostUsd: 500_000,
            replacementCarrierCostUsd: 480_000,
            carrierExitPenaltyUsd: 250_000,
            carrierExitServiceRiskScore: 0.8,
            currency: 'USD',
          },
        ],
      },
      expected: {
        status: 'ok',
        output: {
          primaryMetricKey: 'carrier_exit_net_margin_impact_usd',
          primaryMetricValue: -230_000,
          carrier: 'Ridgeline Freight',
          shipmentCount: 1,
          carrierExitCurrentCostUsd: 500_000,
          carrierExitReplacementCostUsd: 480_000,
          carrierExitContractPenaltyUsd: 250_000,
          carrierExitNetMarginImpactUsd: -230_000,
          carrierExitServiceRiskScore: 0.8,
        },
      },
    },
    {
      // Zero-volume cut: the weighted mean has no basis, so the worst row wins.
      // A zero-volume carrier must never look safer than its riskiest lane.
      traceId: 'carrier_exit_tradeoff_v1:zero-volume-risk-fallback:v1',
      input: {
        carrier: 'Ridgeline Freight',
        reportingCutAsOf: '2026-07-31',
        shipments: [
          {
            shipmentId: 'shp-1',
            volume: 0,
            carrierCostUsd: 100_000,
            replacementCarrierCostUsd: 90_000,
            carrierExitPenaltyUsd: 1_000,
            carrierExitServiceRiskScore: 0.25,
            currency: 'USD',
          },
          {
            shipmentId: 'shp-2',
            volume: 0,
            carrierCostUsd: 50_000,
            replacementCarrierCostUsd: 40_000,
            carrierExitPenaltyUsd: 1_000,
            carrierExitServiceRiskScore: 0.9,
            currency: 'USD',
          },
        ],
      },
      expected: {
        status: 'ok',
        output: {
          primaryMetricKey: 'carrier_exit_net_margin_impact_usd',
          primaryMetricValue: 19_000,
          carrier: 'Ridgeline Freight',
          shipmentCount: 2,
          carrierExitCurrentCostUsd: 150_000,
          carrierExitReplacementCostUsd: 130_000,
          carrierExitContractPenaltyUsd: 1_000,
          carrierExitNetMarginImpactUsd: 19_000,
          carrierExitServiceRiskScore: 0.9,
        },
      },
    },
    {
      traceId: 'carrier_exit_tradeoff_v1:blocked:no-shipments:v1',
      input: {
        carrier: 'Ridgeline Freight',
        reportingCutAsOf: '2026-07-31',
        shipments: [],
      },
      expected: {
        status: 'blocked',
        reasonCode: 'missing_formula_input',
        message:
          'Carrier exit tradeoff requires a carrier, a reporting cut, and at least one governed shipment row.',
      },
    },
    {
      // Partial evidence: a row without a service-risk score inside the domain.
      // The module drops such a carrier; the formula blocks it.
      traceId: 'carrier_exit_tradeoff_v1:blocked:service-risk-out-of-domain:v1',
      input: {
        carrier: 'Ridgeline Freight',
        reportingCutAsOf: '2026-07-31',
        shipments: [
          {
            shipmentId: 'shp-1',
            volume: 100,
            carrierCostUsd: 100_000,
            replacementCarrierCostUsd: 90_000,
            carrierExitPenaltyUsd: 1_000,
            carrierExitServiceRiskScore: 1.4,
            currency: 'USD',
          },
        ],
      },
      expected: {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'Carrier exit shipment rows must carry unique ids, USD amounts that are finite and non-negative, and a service-risk score within [0,1].',
      },
    },
  ],
});

/* -------------------------------------------------------------------------- */
/* SPEC-2 · mode_shift_tradeoff_v1                                             */
/* -------------------------------------------------------------------------- */

export type ModeShiftTradeoffInput = Readonly<{
  reportingCutAsOf: string;
  shipments: readonly Readonly<{
    shipmentId: string;
    targetMode: string;
    volume: number;
    carrierCostUsd: number;
    modeShiftCarrierCostUsd: number;
    modeShiftServiceRiskScore: number;
    currency: 'USD';
  }>[];
}>;

export type ModeShiftTradeoffOutput = Readonly<{
  primaryMetricKey: 'mode_shift_margin_impact_usd';
  primaryMetricValue: number;
  shipmentCount: number;
  targetModes: readonly string[];
  modeShiftEligibleVolume: number;
  modeShiftCurrentCarrierCostUsd: number;
  modeShiftTargetCarrierCostUsd: number;
  modeShiftMarginImpactUsd: number;
  modeShiftServiceRiskScore: number;
}>;

/**
 * Dollar economics of shifting eligible volume to a cheaper transport mode.
 *
 *   current      = SUM(carrierCostUsd)           -- cost on the present mode
 *   target       = SUM(modeShiftCarrierCostUsd)  -- cost on the target mode
 *   margin       = current - target
 *   serviceRisk  = volume-weighted MEAN(modeShiftServiceRiskScore)
 *
 * BOUNDARY — declared law, kept: this formula owns the DOLLAR claim only.
 * The carbon consequence of the same physical shift belongs to
 * `mode_shift_emissions_v1` (esg-emissions), which outputs tCO2e abatement over
 * a `decision_agent:esg_emissions:shipment_tonne_km_snapshot` context. Two
 * separate claims over one physical event: they are never summed, never netted,
 * and neither substitutes for the other.
 *
 * Eligibility (modeShiftEligible, a target mode, a target cost and a risk score
 * all present) is applied by the module seam when it compiles rows; by the time
 * a row reaches this formula it is eligible by construction, and a row missing
 * any governed field is refused rather than defaulted.
 */
function executeModeShiftTradeoff(
  input: ModeShiftTradeoffInput,
): ApprovedFormulaResult<ModeShiftTradeoffOutput> {
  if (
    !input ||
    !isNonEmptyString(input.reportingCutAsOf) ||
    !Array.isArray(input.shipments) ||
    input.shipments.length === 0
  ) {
    return blocked(
      'missing_formula_input',
      'Mode-shift tradeoff requires a reporting cut and at least one eligible governed shipment row.',
    );
  }
  const seen = new Set<string>();
  for (const row of input.shipments) {
    if (
      !row ||
      !isNonEmptyString(row.shipmentId) ||
      seen.has(row.shipmentId) ||
      !isNonEmptyString(row.targetMode) ||
      row.currency !== 'USD' ||
      !isFiniteNonNegative(row.volume) ||
      !isFiniteNonNegative(row.carrierCostUsd) ||
      !isFiniteNonNegative(row.modeShiftCarrierCostUsd) ||
      !isUnitInterval(row.modeShiftServiceRiskScore)
    ) {
      return blocked(
        'invalid_formula_input',
        'Mode-shift shipment rows must carry unique ids, a target mode, USD amounts that are finite and non-negative, and a service-risk score within [0,1].',
      );
    }
    seen.add(row.shipmentId);
  }
  const modeShiftCurrentCarrierCostUsd = roundUsd(
    input.shipments.reduce((sum, row) => sum + row.carrierCostUsd, 0),
  );
  const modeShiftTargetCarrierCostUsd = roundUsd(
    input.shipments.reduce((sum, row) => sum + row.modeShiftCarrierCostUsd, 0),
  );
  const modeShiftEligibleVolume = roundMeasure(
    input.shipments.reduce((sum, row) => sum + row.volume, 0),
  );
  const modeShiftServiceRiskScore = volumeWeightedServiceRisk(
    input.shipments.map((row) => ({
      volume: row.volume,
      serviceRiskScore: row.modeShiftServiceRiskScore,
    })),
    modeShiftEligibleVolume,
  );
  const modeShiftMarginImpactUsd = roundUsd(
    modeShiftCurrentCarrierCostUsd - modeShiftTargetCarrierCostUsd,
  );
  const targetModes = Array.from(new Set(input.shipments.map((row) => row.targetMode))).sort();
  return ok({
    primaryMetricKey: 'mode_shift_margin_impact_usd',
    primaryMetricValue: modeShiftMarginImpactUsd,
    shipmentCount: input.shipments.length,
    targetModes: Object.freeze(targetModes),
    modeShiftEligibleVolume,
    modeShiftCurrentCarrierCostUsd,
    modeShiftTargetCarrierCostUsd,
    modeShiftMarginImpactUsd,
    modeShiftServiceRiskScore,
  });
}

const modeShiftTradeoffDefinition = createDefinition({
  formulaId: 'mode_shift_tradeoff_v1',
  displayName: 'Mode-shift tradeoff',
  description:
    'Sizes the operating-income impact of shifting eligible governed shipment volume to a cheaper transport mode: current carrier cost less target-mode carrier cost, with a volume-weighted service-risk score. Owns the dollar claim only — the carbon consequence of the same shift belongs to mode_shift_emissions_v1 and is never summed with this one.',
  inputSchemaVersion: 'mode_shift_tradeoff_input_v1',
  outputSchemaVersion: 'mode_shift_tradeoff_output_v1',
  execute: executeModeShiftTradeoff,
  helpers: [
    ['isFiniteNonNegative', isFiniteNonNegative],
    ['isNonEmptyString', isNonEmptyString],
    ['isUnitInterval', isUnitInterval],
    ['volumeWeightedServiceRisk', volumeWeightedServiceRisk],
  ],
  goldenTraces: [
    {
      // Rail is cheaper than the incumbent truck lane: 1.2M -> 900k saves 300k.
      traceId: 'mode_shift_tradeoff_v1:shift-saves:v1',
      input: {
        reportingCutAsOf: '2026-07-31',
        shipments: [
          {
            shipmentId: 'shp-1',
            targetMode: 'rail',
            volume: 800,
            carrierCostUsd: 700_000,
            modeShiftCarrierCostUsd: 500_000,
            modeShiftServiceRiskScore: 0.5,
            currency: 'USD',
          },
          {
            shipmentId: 'shp-2',
            targetMode: 'rail',
            volume: 200,
            carrierCostUsd: 500_000,
            modeShiftCarrierCostUsd: 400_000,
            modeShiftServiceRiskScore: 0.3,
            currency: 'USD',
          },
        ],
      },
      expected: {
        status: 'ok',
        output: {
          primaryMetricKey: 'mode_shift_margin_impact_usd',
          primaryMetricValue: 300_000,
          shipmentCount: 2,
          targetModes: ['rail'],
          modeShiftEligibleVolume: 1_000,
          modeShiftCurrentCarrierCostUsd: 1_200_000,
          modeShiftTargetCarrierCostUsd: 900_000,
          modeShiftMarginImpactUsd: 300_000,
          // (0.5*800 + 0.3*200) / 1000 = 0.46
          modeShiftServiceRiskScore: 0.46,
        },
      },
    },
    {
      // The target mode costs MORE. A shift can be a service or carbon decision
      // that loses money; the dollar claim must report the loss, not hide it.
      traceId: 'mode_shift_tradeoff_v1:shift-costs-more:v1',
      input: {
        reportingCutAsOf: '2026-07-31',
        shipments: [
          {
            shipmentId: 'shp-1',
            targetMode: 'air',
            volume: 100,
            carrierCostUsd: 200_000,
            modeShiftCarrierCostUsd: 340_000,
            modeShiftServiceRiskScore: 0.1,
            currency: 'USD',
          },
        ],
      },
      expected: {
        status: 'ok',
        output: {
          primaryMetricKey: 'mode_shift_margin_impact_usd',
          primaryMetricValue: -140_000,
          shipmentCount: 1,
          targetModes: ['air'],
          modeShiftEligibleVolume: 100,
          modeShiftCurrentCarrierCostUsd: 200_000,
          modeShiftTargetCarrierCostUsd: 340_000,
          modeShiftMarginImpactUsd: -140_000,
          modeShiftServiceRiskScore: 0.1,
        },
      },
    },
    {
      traceId: 'mode_shift_tradeoff_v1:blocked:no-eligible-rows:v1',
      input: {
        reportingCutAsOf: '2026-07-31',
        shipments: [],
      },
      expected: {
        status: 'blocked',
        reasonCode: 'missing_formula_input',
        message:
          'Mode-shift tradeoff requires a reporting cut and at least one eligible governed shipment row.',
      },
    },
    {
      traceId: 'mode_shift_tradeoff_v1:blocked:missing-target-mode:v1',
      input: {
        reportingCutAsOf: '2026-07-31',
        shipments: [
          {
            shipmentId: 'shp-1',
            targetMode: '',
            volume: 100,
            carrierCostUsd: 200_000,
            modeShiftCarrierCostUsd: 150_000,
            modeShiftServiceRiskScore: 0.1,
            currency: 'USD',
          },
        ],
      },
      expected: {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'Mode-shift shipment rows must carry unique ids, a target mode, USD amounts that are finite and non-negative, and a service-risk score within [0,1].',
      },
    },
  ],
});

/* -------------------------------------------------------------------------- */
/* SPEC-3 · specialist_network_option_score_v1                                 */
/* -------------------------------------------------------------------------- */

export type SpecialistNetworkOptionScoreInput = Readonly<{
  optionSetId: string;
  reportingCutAsOf: string;
  options: readonly Readonly<{
    optionId: string;
    marginImpactUsd: number;
    serviceRiskScore: number;
    confidenceScore: number | null;
    strategicFit: string | null;
  }>[];
}>;

export type SpecialistNetworkOptionScoreOutput = Readonly<{
  primaryMetricKey: 'network_reconfiguration_margin_impact_usd';
  primaryMetricValue: number;
  optionSetId: string;
  optionCount: number;
  topOptionId: string;
  rankedOptions: readonly Readonly<{
    optionId: string;
    optionScore: number;
    networkReconfigurationMarginImpactUsd: number;
    networkReconfigurationServiceRiskScore: number;
    networkReconfigurationConfidenceScore: number | null;
  }>[];
}>;

const SPECIALIST_NETWORK_DEFAULT_CONFIDENCE = 0.75;
const SPECIALIST_NETWORK_STRATEGIC_FIT_PATTERN = /aligned|fit|growth|strategic|service/i;
const SPECIALIST_NETWORK_STRATEGIC_MULTIPLIER = 1.15;
const SPECIALIST_NETWORK_SERVICE_PENALTY_WEIGHT = 0.5;

/**
 * Risk- and confidence-adjusted ranking score for one admitted network option.
 *
 *   score = marginImpactUsd
 *         * confidence                       (confidenceScore, default 0.75)
 *         * strategicMultiplier              (1.15 when the governed strategic-fit
 *                                             text matches, else 1)
 *         * (1 - min(max(serviceRisk,0),1) * 0.5)
 *
 * The 0.75 default is the module's declared posture for an option whose provider
 * supplied no confidence: assume neither certainty nor the worst case. It is a
 * ranking weight only — it never scales a published dollar figure.
 */
function specialistNetworkOptionScore(option: {
  marginImpactUsd: number;
  serviceRiskScore: number;
  confidenceScore: number | null;
  strategicFit: string | null;
}): number {
  const confidence = option.confidenceScore ?? SPECIALIST_NETWORK_DEFAULT_CONFIDENCE;
  const servicePenalty = Math.max(0, Math.min(1, option.serviceRiskScore));
  const strategicMultiplier = SPECIALIST_NETWORK_STRATEGIC_FIT_PATTERN.test(
    option.strategicFit ?? '',
  )
    ? SPECIALIST_NETWORK_STRATEGIC_MULTIPLIER
    : 1;
  return (
    option.marginImpactUsd *
    confidence *
    strategicMultiplier *
    (1 - servicePenalty * SPECIALIST_NETWORK_SERVICE_PENALTY_WEIGHT)
  );
}

/**
 * Scores and ranks an admitted specialist-network option set.
 *
 * THE FORMULA SCORES; IT DOES NOT OPTIMIZE. `marginImpactUsd` arrives from a
 * third-party network optimizer whose output is admitted as governed evidence.
 * This formula never re-derives that dollar figure — it applies the module's
 * declared confidence and service-risk adjustment to rank the options, and
 * republishes the admitted margin impact unchanged. The published claim is the
 * top-ranked option's margin impact, which is why the ranking is part of the
 * output and auditable rather than an internal sort.
 *
 * Ties break on margin impact, then on optionId, so the ranking is total and
 * reproducible.
 */
function executeSpecialistNetworkOptionScore(
  input: SpecialistNetworkOptionScoreInput,
): ApprovedFormulaResult<SpecialistNetworkOptionScoreOutput> {
  if (
    !input ||
    !isNonEmptyString(input.optionSetId) ||
    !isNonEmptyString(input.reportingCutAsOf) ||
    !Array.isArray(input.options) ||
    input.options.length === 0
  ) {
    return blocked(
      'missing_formula_input',
      'Specialist network option scoring requires an option set, a reporting cut, and at least one admitted governed option.',
    );
  }
  const seen = new Set<string>();
  for (const option of input.options) {
    if (
      !option ||
      !isNonEmptyString(option.optionId) ||
      seen.has(option.optionId) ||
      !isFiniteNumber(option.marginImpactUsd) ||
      !isUnitInterval(option.serviceRiskScore) ||
      (option.confidenceScore !== null && !isUnitInterval(option.confidenceScore))
    ) {
      return blocked(
        'invalid_formula_input',
        'Specialist network options must carry unique ids, a finite USD margin impact, and service-risk and confidence scores within [0,1].',
      );
    }
    seen.add(option.optionId);
  }
  const ranked = input.options
    .map((option) => ({ option, score: specialistNetworkOptionScore(option) }))
    .sort(
      (left, right) =>
        right.score - left.score ||
        right.option.marginImpactUsd - left.option.marginImpactUsd ||
        left.option.optionId.localeCompare(right.option.optionId),
    )
    .map(({ option, score }) =>
      Object.freeze({
        optionId: option.optionId,
        optionScore: roundMeasure(score),
        networkReconfigurationMarginImpactUsd: roundUsd(option.marginImpactUsd),
        networkReconfigurationServiceRiskScore: roundMeasure(option.serviceRiskScore),
        networkReconfigurationConfidenceScore:
          option.confidenceScore === null ? null : roundMeasure(option.confidenceScore),
      }),
    );
  const top = ranked[0];
  return ok({
    primaryMetricKey: 'network_reconfiguration_margin_impact_usd',
    primaryMetricValue: top.networkReconfigurationMarginImpactUsd,
    optionSetId: input.optionSetId,
    optionCount: ranked.length,
    topOptionId: top.optionId,
    rankedOptions: Object.freeze(ranked),
  });
}

const specialistNetworkOptionScoreDefinition = createDefinition({
  formulaId: 'specialist_network_option_score_v1',
  displayName: 'Specialist network option score',
  description:
    'Ranks an admitted specialist-network optimizer option set by confidence- and service-risk-adjusted score, and publishes the top-ranked option operating-income impact. Scores admitted evidence; it never re-optimizes the network or re-derives the margin impact.',
  inputSchemaVersion: 'specialist_network_option_score_input_v1',
  outputSchemaVersion: 'specialist_network_option_score_output_v1',
  execute: executeSpecialistNetworkOptionScore,
  helpers: [
    ['isFiniteNumber', isFiniteNumber],
    ['isNonEmptyString', isNonEmptyString],
    ['isUnitInterval', isUnitInterval],
    ['specialistNetworkOptionScore', specialistNetworkOptionScore],
  ],
  goldenTraces: [
    {
      // The larger raw dollar option loses to the smaller one: high service risk
      // and low confidence outrank a bigger headline number.
      traceId: 'specialist_network_option_score_v1:risk-reranks:v1',
      input: {
        optionSetId: 'optset-2026-07',
        reportingCutAsOf: '2026-07-31',
        options: [
          {
            optionId: 'opt-big-risky',
            marginImpactUsd: 4_000_000,
            serviceRiskScore: 0.9,
            confidenceScore: 0.4,
            strategicFit: null,
          },
          {
            optionId: 'opt-solid',
            marginImpactUsd: 3_000_000,
            serviceRiskScore: 0.2,
            confidenceScore: 0.9,
            strategicFit: 'strategic corridor aligned',
          },
        ],
      },
      expected: {
        status: 'ok',
        output: {
          primaryMetricKey: 'network_reconfiguration_margin_impact_usd',
          primaryMetricValue: 3_000_000,
          optionSetId: 'optset-2026-07',
          optionCount: 2,
          topOptionId: 'opt-solid',
          rankedOptions: [
            {
              optionId: 'opt-solid',
              // 3e6 * 0.9 * 1.15 * (1 - 0.1) = 2_794_500
              optionScore: 2_794_500,
              networkReconfigurationMarginImpactUsd: 3_000_000,
              networkReconfigurationServiceRiskScore: 0.2,
              networkReconfigurationConfidenceScore: 0.9,
            },
            {
              optionId: 'opt-big-risky',
              // 4e6 * 0.4 * 1 * (1 - 0.45) = 880_000
              optionScore: 880_000,
              networkReconfigurationMarginImpactUsd: 4_000_000,
              networkReconfigurationServiceRiskScore: 0.9,
              networkReconfigurationConfidenceScore: 0.4,
            },
          ],
        },
      },
    },
    {
      // No confidence supplied by the provider: the declared 0.75 default
      // applies and is visible in the score, while the published confidence
      // stays null rather than pretending a governed 0.75 was supplied.
      traceId: 'specialist_network_option_score_v1:default-confidence:v1',
      input: {
        optionSetId: 'optset-2026-07',
        reportingCutAsOf: '2026-07-31',
        options: [
          {
            optionId: 'opt-unrated',
            marginImpactUsd: 1_000_000,
            serviceRiskScore: 0,
            confidenceScore: null,
            strategicFit: null,
          },
        ],
      },
      expected: {
        status: 'ok',
        output: {
          primaryMetricKey: 'network_reconfiguration_margin_impact_usd',
          primaryMetricValue: 1_000_000,
          optionSetId: 'optset-2026-07',
          optionCount: 1,
          topOptionId: 'opt-unrated',
          rankedOptions: [
            {
              optionId: 'opt-unrated',
              optionScore: 750_000,
              networkReconfigurationMarginImpactUsd: 1_000_000,
              networkReconfigurationServiceRiskScore: 0,
              networkReconfigurationConfidenceScore: null,
            },
          ],
        },
      },
    },
    {
      traceId: 'specialist_network_option_score_v1:blocked:no-options:v1',
      input: {
        optionSetId: 'optset-2026-07',
        reportingCutAsOf: '2026-07-31',
        options: [],
      },
      expected: {
        status: 'blocked',
        reasonCode: 'missing_formula_input',
        message:
          'Specialist network option scoring requires an option set, a reporting cut, and at least one admitted governed option.',
      },
    },
    {
      traceId: 'specialist_network_option_score_v1:blocked:confidence-out-of-domain:v1',
      input: {
        optionSetId: 'optset-2026-07',
        reportingCutAsOf: '2026-07-31',
        options: [
          {
            optionId: 'opt-1',
            marginImpactUsd: 1_000_000,
            serviceRiskScore: 0.2,
            confidenceScore: 1.6,
            strategicFit: null,
          },
        ],
      },
      expected: {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'Specialist network options must carry unique ids, a finite USD margin impact, and service-risk and confidence scores within [0,1].',
      },
    },
  ],
});

/* -------------------------------------------------------------------------- */
/* SPEC-4 · customer_repricing_volume_response_v1                              */
/* -------------------------------------------------------------------------- */

export type CustomerRepricingVolumeResponseInput = Readonly<{
  priceIncreasePct: number;
  reportingCutAsOf: string;
  shipments: readonly Readonly<{
    shipmentId: string;
    volume: number;
    revenueUsd: number;
    carrierCostUsd: number;
    customerPriceElasticity: number;
    currency: 'USD';
  }>[];
}>;

export type CustomerRepricingVolumeResponseOutput = Readonly<{
  primaryMetricKey: 'transportation_margin_delta_usd';
  primaryMetricValue: number;
  shipmentCount: number;
  priceIncreasePct: number;
  shipmentLaneVolumeDelta: number;
  transportationRevenueDeltaUsd: number;
  transportationCarrierCostDeltaUsd: number;
  transportationMarginDeltaUsd: number;
}>;

/**
 * Lane-level margin response to a customer price increase, driven by governed
 * per-row price elasticity.
 *
 *   lossRatio(i)  = min(1, max(0, |elasticity(i)| * priceIncreasePct))
 *   revenue'      = SUM(revenue(i) * (1 - lossRatio(i)) * (1 + priceIncreasePct))
 *   carrierCost'  = SUM(carrierCost(i) * (1 - lossRatio(i)))
 *   volumeDelta   = -SUM(volume(i) * lossRatio(i))
 *   revenueDelta  = revenue' - SUM(revenue(i))
 *   costDelta     = carrierCost' - SUM(carrierCost(i))
 *   marginDelta   = revenueDelta - costDelta
 *
 * The loss ratio is clamped to [0,1]: elasticity can never destroy more than
 * all of a lane's volume, and a positive-signed elasticity is read by magnitude
 * because a price rise loses volume regardless of the sign convention the
 * provider used.
 *
 * marginDelta is computed from the ROUNDED revenue and cost deltas, matching the
 * module exactly. Recomputing it from unrounded sums could disagree with the
 * published components by cents and break reconciliation against them.
 *
 * DE-CONFLICTION — three repricing formulas exist and none may be summed:
 *   * `demand_revenue_repricing_v1`  — macro gross-revenue sensitivity over a
 *     P&L baseline (`decision_agent:scenario_chain:*`), owned by ask-helix/lrp.
 *   * `freight_cost_repricing_v1`    — purchased-transport rate sensitivity.
 *   * THIS ONE                       — lane-level, elasticity-driven, and
 *     carrier-cost aware: the only one that nets the variable cost released by
 *     the volume it loses.
 * Whether one of the macro formulas should instead own this claim is an
 * ownership ruling for the captain; this formula does not assume it.
 */
function executeCustomerRepricingVolumeResponse(
  input: CustomerRepricingVolumeResponseInput,
): ApprovedFormulaResult<CustomerRepricingVolumeResponseOutput> {
  if (
    !input ||
    !isNonEmptyString(input.reportingCutAsOf) ||
    !Array.isArray(input.shipments) ||
    input.shipments.length === 0
  ) {
    return blocked(
      'missing_formula_input',
      'Customer repricing volume response requires a reporting cut and at least one governed shipment row.',
    );
  }
  if (!isFiniteNumber(input.priceIncreasePct) || input.priceIncreasePct <= 0) {
    return blocked(
      'invalid_formula_input',
      'Customer repricing volume response requires a positive finite priceIncreasePct.',
    );
  }
  const seen = new Set<string>();
  for (const row of input.shipments) {
    if (
      !row ||
      !isNonEmptyString(row.shipmentId) ||
      seen.has(row.shipmentId) ||
      row.currency !== 'USD' ||
      !isFiniteNonNegative(row.volume) ||
      !isFiniteNonNegative(row.revenueUsd) ||
      !isFiniteNonNegative(row.carrierCostUsd) ||
      !isFiniteNumber(row.customerPriceElasticity)
    ) {
      return blocked(
        'invalid_formula_input',
        'Customer repricing rows must carry unique ids, finite non-negative USD revenue, carrier cost and volume, and a finite governed price elasticity.',
      );
    }
    seen.add(row.shipmentId);
  }
  let baselineRevenueUsd = 0;
  let baselineCarrierCostUsd = 0;
  let scenarioRevenueUsd = 0;
  let scenarioCarrierCostUsd = 0;
  let volumeLoss = 0;
  for (const row of input.shipments) {
    const lossRatio = Math.min(
      1,
      Math.max(0, Math.abs(row.customerPriceElasticity) * input.priceIncreasePct),
    );
    baselineRevenueUsd += row.revenueUsd;
    baselineCarrierCostUsd += row.carrierCostUsd;
    scenarioRevenueUsd += row.revenueUsd * (1 - lossRatio) * (1 + input.priceIncreasePct);
    scenarioCarrierCostUsd += row.carrierCostUsd * (1 - lossRatio);
    volumeLoss += row.volume * lossRatio;
  }
  const transportationRevenueDeltaUsd = roundUsd(scenarioRevenueUsd - baselineRevenueUsd);
  const transportationCarrierCostDeltaUsd = roundUsd(
    scenarioCarrierCostUsd - baselineCarrierCostUsd,
  );
  const transportationMarginDeltaUsd = roundUsd(
    transportationRevenueDeltaUsd - transportationCarrierCostDeltaUsd,
  );
  const shipmentLaneVolumeDelta = -roundMeasure(volumeLoss);
  return ok({
    primaryMetricKey: 'transportation_margin_delta_usd',
    primaryMetricValue: transportationMarginDeltaUsd,
    shipmentCount: input.shipments.length,
    priceIncreasePct: input.priceIncreasePct,
    shipmentLaneVolumeDelta,
    transportationRevenueDeltaUsd,
    transportationCarrierCostDeltaUsd,
    transportationMarginDeltaUsd,
  });
}

const customerRepricingVolumeResponseDefinition = createDefinition({
  formulaId: 'customer_repricing_volume_response_v1',
  displayName: 'Customer repricing volume response',
  description:
    'Sizes the lane-level operating-income response to a customer price increase using governed per-shipment price elasticity: revenue gained on retained volume, less the revenue lost to volume response, net of the variable carrier cost released. Distinct from the macro repricing formulas and never summed with them.',
  inputSchemaVersion: 'customer_repricing_volume_response_input_v1',
  outputSchemaVersion: 'customer_repricing_volume_response_output_v1',
  execute: executeCustomerRepricingVolumeResponse,
  helpers: [
    ['isFiniteNumber', isFiniteNumber],
    ['isFiniteNonNegative', isFiniteNonNegative],
    ['isNonEmptyString', isNonEmptyString],
  ],
  goldenTraces: [
    {
      // 10% price rise, elasticity 0.5 -> 5% volume loss.
      // revenue' = 1_000_000 * 0.95 * 1.10 = 1_045_000 -> +45_000
      // cost'    =   600_000 * 0.95        =   570_000 -> -30_000
      // margin   = 45_000 - (-30_000)      =  +75_000
      traceId: 'customer_repricing_volume_response_v1:elastic-accretive:v1',
      input: {
        priceIncreasePct: 0.1,
        reportingCutAsOf: '2026-07-31',
        shipments: [
          {
            shipmentId: 'shp-1',
            volume: 1_000,
            revenueUsd: 1_000_000,
            carrierCostUsd: 600_000,
            customerPriceElasticity: 0.5,
            currency: 'USD',
          },
        ],
      },
      expected: {
        status: 'ok',
        output: {
          primaryMetricKey: 'transportation_margin_delta_usd',
          primaryMetricValue: 75_000,
          shipmentCount: 1,
          priceIncreasePct: 0.1,
          shipmentLaneVolumeDelta: -50,
          transportationRevenueDeltaUsd: 45_000,
          transportationCarrierCostDeltaUsd: -30_000,
          transportationMarginDeltaUsd: 75_000,
        },
      },
    },
    {
      // Highly elastic lane: |elasticity| * priceIncreasePct = 1.2, clamped to
      // 1.0. All volume is lost, revenue and cost both go to zero, and the
      // margin delta is exactly the baseline margin destroyed. The clamp is what
      // stops the arithmetic from inventing negative volume.
      traceId: 'customer_repricing_volume_response_v1:loss-ratio-clamped:v1',
      input: {
        priceIncreasePct: 0.2,
        reportingCutAsOf: '2026-07-31',
        shipments: [
          {
            shipmentId: 'shp-1',
            volume: 500,
            revenueUsd: 400_000,
            carrierCostUsd: 300_000,
            customerPriceElasticity: -6,
            currency: 'USD',
          },
        ],
      },
      expected: {
        status: 'ok',
        output: {
          primaryMetricKey: 'transportation_margin_delta_usd',
          primaryMetricValue: -100_000,
          shipmentCount: 1,
          priceIncreasePct: 0.2,
          shipmentLaneVolumeDelta: -500,
          transportationRevenueDeltaUsd: -400_000,
          transportationCarrierCostDeltaUsd: -300_000,
          transportationMarginDeltaUsd: -100_000,
        },
      },
    },
    {
      traceId: 'customer_repricing_volume_response_v1:blocked:non-positive-price-increase:v1',
      input: {
        priceIncreasePct: 0,
        reportingCutAsOf: '2026-07-31',
        shipments: [
          {
            shipmentId: 'shp-1',
            volume: 100,
            revenueUsd: 100_000,
            carrierCostUsd: 60_000,
            customerPriceElasticity: 0.5,
            currency: 'USD',
          },
        ],
      },
      expected: {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'Customer repricing volume response requires a positive finite priceIncreasePct.',
      },
    },
    {
      // The module blocks when ANY row lacks governed elasticity. A missing
      // coefficient is never read as zero — that would publish a repricing with
      // no volume response at all.
      traceId: 'customer_repricing_volume_response_v1:blocked:missing-elasticity:v1',
      input: {
        priceIncreasePct: 0.1,
        reportingCutAsOf: '2026-07-31',
        shipments: [
          {
            shipmentId: 'shp-1',
            volume: 100,
            revenueUsd: 100_000,
            carrierCostUsd: 60_000,
            customerPriceElasticity: Number.NaN,
            currency: 'USD',
          },
        ],
      },
      expected: {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'Customer repricing rows must carry unique ids, finite non-negative USD revenue, carrier cost and volume, and a finite governed price elasticity.',
      },
    },
    {
      traceId: 'customer_repricing_volume_response_v1:blocked:no-shipments:v1',
      input: {
        priceIncreasePct: 0.1,
        reportingCutAsOf: '2026-07-31',
        shipments: [],
      },
      expected: {
        status: 'blocked',
        reasonCode: 'missing_formula_input',
        message:
          'Customer repricing volume response requires a reporting cut and at least one governed shipment row.',
      },
    },
  ],
});

export const CARRIER_TRANSPORTATION_APPROVED_FORMULA_DEFINITIONS: readonly ApprovedFormulaDefinition[] =
  Object.freeze([
    carrierExitTradeoffDefinition,
    modeShiftTradeoffDefinition,
    specialistNetworkOptionScoreDefinition,
    customerRepricingVolumeResponseDefinition,
  ]);
